import { Fragment, FC, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { KeyboardArrowDown } from "@mui/icons-material"
import { Userbot } from "../../interfaces"
import { useChainReportQuery } from "../../store"
import { getApiErrorMessage } from "@/core/utils"
import { blockBackdropClose } from "../common/dialogUtils"
import { previewJson } from "../common/jsonUtils"
import { formatStepFailure } from "./stepFailure"
import { ReportProvenance } from "./reportProvenance"
import { ChainProgressPanel } from "./chainProgressPanel"
import { StepResultDetails } from "./stepResultDetails"

interface IProps {
  chainId: string
  onClose: () => void
}

const statusColor = (
  status: Userbot.ChainReportStep["status"],
): "success" | "error" | "warning" => {
  if (status === "completed") return "success"
  if (status === "failed") return "error"
  return "warning"
}

export const ActiveChainReport: FC<IProps> = ({ chainId, onClose }) => {
  const { data, isFetching, isError, error } = useChainReportQuery(
    { id: chainId },
    { pollingInterval: 60000 },
  )

  const [expandedStepId, setExpandedStepId] = useState<string | null>(null)
  const toggleExpand = (id: string) =>
    setExpandedStepId((prev) => (prev === id ? null : id))

  return (
    <Dialog open onClose={blockBackdropClose(onClose)} maxWidth="lg" fullWidth>
      <DialogTitle>Отчёт по цепочке {data?.name ?? chainId}</DialogTitle>
      <DialogContent>
        {isFetching && !data && <CircularProgress />}
        {isError && <Alert severity="error">{getApiErrorMessage(error)}</Alert>}
        {data && (
          <>
            <Box mb={2}>
              <ChainProgressPanel chainId={chainId} />
            </Box>
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              <Chip color="success" label={`completed: ${data.completed}`} />
              <Chip color="error" label={`failed: ${data.failed}`} />
              <Chip color="warning" label={`skipped: ${data.skipped}`} />
            </Box>
            <ReportProvenance report={data} />
            {data.steps.length === 0 ? (
              <Typography color="text.secondary">
                Шаги ещё не выполнялись.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Шаг</TableCell>
                    <TableCell>Действие</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Попыток</TableCell>
                    <TableCell>Ошибка</TableCell>
                    <TableCell>Результат</TableCell>
                    <TableCell>Завершён</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.steps.map((s) => (
                    <Fragment key={s.stepId}>
                      <TableRow hover>
                        <TableCell>{s.stepId}</TableCell>
                        <TableCell>{s.action}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={statusColor(s.status)}
                            label={s.status}
                          />
                        </TableCell>
                        <TableCell>{s.attemptsMade}</TableCell>
                        <TableCell>{formatStepFailure(s)}</TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: "monospace" }}
                            >
                              {previewJson(s.result)}
                            </Typography>
                            <IconButton
                              size="small"
                              aria-label={`Детали результата ${s.stepId}`}
                              onClick={() => toggleExpand(s.stepId)}
                            >
                              <KeyboardArrowDown
                                sx={{
                                  transform:
                                    expandedStepId === s.stepId
                                      ? "rotate(180deg)"
                                      : undefined,
                                  transition: "transform .2s",
                                }}
                              />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {s.finishedOn
                            ? new Date(s.finishedOn).toLocaleString("ru")
                            : "—"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{
                            p: 0,
                            borderBottom:
                              expandedStepId === s.stepId ? undefined : "none",
                          }}
                        >
                          <Collapse
                            in={expandedStepId === s.stepId}
                            unmountOnExit
                          >
                            <StepResultDetails step={s} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  )
}
